/**
 * MONSTER TOWN — FISHING, BUG CATCHING AND THE BOOK. CREW ENTRY POINT.
 *
 * One `install` line in `main.ts`, and everything this crew owns comes up:
 *
 *   places.ts    what tag is under this patch of town ('pond' / 'tree' / ...)
 *   bugs.ts      creatures wandering the grass and the trees, tap to net
 *   fishing.ts   tap to cast, wait for the dip, tap to catch — no losing
 *   reward.ts    what a catch is worth, and the page-completion bonus
 *   ui/collection.ts   the book, and the "new page!" moment
 *
 * NOTHING IS SAVED FROM THIS CREW. The book lives in the architect's
 * `collection` save section and the bag in `inventory`; a bug in the grass and
 * a float on the water are transient by design — they come back with the next
 * spawn tick, and the clock decides which ones. Saves are sacred, and the
 * safest new save section is the one that does not exist.
 *
 * ORDER: install after the HUD (this crew appends a button to its rail) and
 * after the world (it asks the world where the trees are).
 */

import type { SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { registerSpot } from '../harness'
import { installBugs, bugReport, clearBugs, forceBug, forceNet, tapDiagnosis } from './bugs'
import { forceBite, forceCast, forceLand, forceMiss, installFishing, reelIn, rodReport } from './fishing'
import { installPlaces, isPaved, placeTagAt, pondStand } from './places'
import { installPageBonus } from './reward'
import { cancelWalks, installWalkWatchdog, pendingWalks } from './walk'
import { bus } from '../core/events'
import { app } from '../app/context'
import { installCollectionBook } from '../ui/collection'

let installed = false

export function installCatching(ctx: GameContext): void {
  if (installed) return
  installed = true
  installPlaces()
  installWalkWatchdog()
  // Finishing ANY page of the book pays — a recipe page and an animal page as
  // much as a bug page. See the header of `reward.ts`.
  installPageBonus(() => ({ ...app.playerPos }))
  installBugs(ctx)
  installFishing(ctx)
  installCollectionBook(ctx)

  /**
   * A door ends every errand this crew is in the middle of. Without this, a kid
   * who taps a butterfly and then walks through their own front door has a
   * five-second watchdog still counting down in another scene.
   */
  bus.on('scene:transitionStart', () => cancelWalks())

  /**
   * A named SPOT, not a named angle: `drive` sweeps every registered angle and
   * pays a settle for each one, and that step is already close to its own
   * timeout with thirty-odd of them. Spots are only ever resolved on demand.
   *
   * ===========================================================================
   *  IT IS RE-REGISTERED AFTER THE TOWN IS BUILT, AND THAT IS THE FIX.
   * ===========================================================================
   * Round `vr2`: "the monster fishes from the middle of the road... it stands on
   * the pedestrian crossing several metres from the water." Half of that was
   * `pondStand` (fixed in `places.ts`) and half of it was HERE.
   *
   * `src/world/town.ts` also registers a spot called `pond`, from the pond
   * LANDMARK's own `standAt` — and it does so while building the town, which is
   * after every crew's `install()`. So this line ran first and was overwritten,
   * and every fishing tool in `tools/` that says `teleport('pond')` has been
   * putting the monster on the world crew's landmark stand point ever since.
   * A landmark stand point answers "walk over and look at it"; it is not an
   * answer to "stand where you can fish", and at x −19 it is the ring road.
   *
   * `registerSpot` is a public harness API several crews call and the last
   * writer wins by design, so this crew simply answers last: the pond is a place
   * you FISH from. Nothing in `src/world/**` is touched, and re-resolving after
   * the scene is built is also more honest than resolving at install time, when
   * the grid `walkableNear` consults has not been baked yet.
   */
  const registerBank = (): void => {
    registerSpot('pond', 'town' as SceneId, pondStand({ x: 0, y: 0, z: -4 }))
  }
  registerBank()
  /**
   * ON THE NEXT MACROTASK, and that is not superstition. The world crew writes
   * its `pond` spot from `registerHarnessViews()`, which `src/world/town.ts`
   * defers with `setTimeout(..., 0)` — so it lands after `app:booted`, after
   * `save:loaded` and after every synchronous install in `main.ts`. A timer
   * queued from THIS event therefore runs after theirs, because theirs was
   * queued first and the task queue is FIFO. `scene:entered` covers a later
   * rebuild (a slot switch, a cleared plot); a tool that never leaves the town
   * would otherwise never see one.
   */
  bus.on('app:booted', () => {
    window.setTimeout(registerBank, 0)
  })
  bus.on('scene:entered', (e) => {
    if (String(e.scene) === 'town') registerBank()
  })

  /**
   * The test surface for `tools/catch-check.mjs`. Its own global, exactly like
   * `window.__ui` and `window.__tut` — `window.__harness` is architect-owned and
   * already carries the CONTRACT-level calls (`findCollectible`, `availableNow`,
   * `collectionState`). What is here is the parts a tool cannot reach any other
   * way: is there really a bug in the world, is the float really dipping.
   */
  window.__catch = {
    ready: true,
    bugs: () => bugReport(),
    rod: () => rodReport(),
    placeAt: (x, z) => placeTagAt(x, z),
    paved: (x, z) => isPaved(x, z),
    spawnBug: (id) => forceBug(id),
    netBug: () => forceNet(),
    clearBugs: () => clearBugs(),
    cast: () => forceCast(),
    bite: () => forceBite(),
    land: () => forceLand(),
    miss: () => forceMiss(),
    reelIn: () => reelIn(),
    walking: () => pendingWalks(),
    whyLastTap: () => tapDiagnosis(),
  }
}

export interface CatchTestApi {
  readonly ready: boolean
  bugs(): Array<{ id: string; place: string; at: { x: number; y: number; z: number }; found: boolean; onScreen: boolean }>
  rod(): {
    state: string
    hooked: string | null
    left: number
    water: { x: number; y: number; z: number } | null
    shout: boolean
    bobY: number
    /** World position of the rod tip and of the bite burst. See `RodReport`. */
    tip: { x: number; y: number; z: number } | null
    shoutAt: { x: number; y: number; z: number } | null
  }
  placeAt(x: number, z: number): string
  /**
   * Is this patch of town ROAD, PAVEMENT or PLAZA? Tools only, and it exists
   * because `placeAt` cannot answer it: the tag for a zebra crossing and the tag
   * for a lawn are both 'grass', which is exactly how round `vr1` came to find a
   * ladybird standing on the crossing. See `isPaved` in `places.ts`.
   */
  paved(x: number, z: number): boolean
  spawnBug(id?: string): string | null
  netBug(): string | null
  clearBugs(): void
  cast(): boolean
  bite(): string | null
  land(): boolean
  miss(): boolean
  reelIn(): void
  /** How many "walk over there and then do it" errands are in flight. */
  walking(): number
  /** Why the last tap was or was not a catch. Tools only. */
  whyLastTap(): string
}

declare global {
  interface Window {
    __catch?: CatchTestApi
  }
}
